import * as THREE from 'three'

import Config from '@glass-designer/config'
import Types from '../../types'
import GlassBaseHole from './../glassBaseHole'
import MathsUtils from '@/utils/mathsUtils'
import GlassSegment from '@/model/glassSegment'

import GlassDesignerTypes from '../../types'


/*
 * CircleHole
 * Simple circle hole with a radius
 */
export default class CircleHole extends GlassBaseHole {
  constructor(editor, options = {}) {
    super(editor, options)
    this.objectClassName = 'CircleHole'
    
    this.name = 'Circle Center Cutout'
    this.holeType = GlassDesignerTypes.LibraryItemShapeTypes.circle
    this.defaultRadius = 20
    
    this.radius = MathsUtils.roundNumber(options.radius || this.defaultRadius, 1)

    this.csOutlineMaterial = null
    this.csOutlineObj = null

    this.rebuildHoleShape()
  }

  dispose() {
    super.dispose()

    if(this.csOutlineObj) {
      this.csOutlineObj.geometry.dispose()
      this.csOutlineObj = null
    }
  }


  /**
   * Update the center cutout settings
   */
  updateCutoutSettings(info) {
    if(info.radius !== undefined)
      this.radius = parseFloat(info.radius || 0)

    super.updateCutoutSettings(info)
  }


  /**
   * Rotate the hole
   * Overriden by the shape classes
   */
  rotateHole(angleDegrees) {
    super.rotateHole(angleDegrees)
  }


  /**
   * Rebuilds the hole segments based on the basic width/height of the rectangle hole
   * Only to be used when first creating the hole
   */
  rebuildHoleShape() {
    var startX = 0
    var startY = 0

    // Update via datum links
    this.updateDatumLinkWorldPoint()
    startX = this.worldPoint.x - this.anchorPoint.x
    startY = this.worldPoint.y - this.anchorPoint.y

    if(this.datumDisplayPointObj)
      this.datumDisplayPointObj.setPosition(this.worldPoint)

    var radius = Math.max(this.radius, 1)

    this.segmentList = []

    // Two 180 arcs
    let segmentA = new GlassSegment(Types.SegmentTypes.arc, {
      arcRadius: radius,
      arcStartAngle: 0,
      arcEndAngle: 180,
      arcClockwise: true
    })
    segmentA.arcCenterPosition.set(startX + radius, startY + radius)
    segmentA.arcUpdated()
    this.segmentList.push(segmentA)

    let segmentB = new GlassSegment(Types.SegmentTypes.arc, {
      arcRadius: radius,
      arcStartAngle: 180,
      arcEndAngle: 360,
      arcClockwise: true
    })
    segmentB.arcCenterPosition.set(startX + radius, startY + radius)
    segmentB.arcUpdated()
    this.segmentList.push(segmentB)

    MathsUtils.segmentListUpdated(this.segmentList)
    
    this.updateSceneObject()
  }


  /**
   * Update the selection display object from the hole shape segments
   * Called when rebuilding the hole shape
   */
   updateSceneObject() {
    var shapeDivisions = Config.Settings.arcSubdivisions

    if(this.csOutlineObj) {
      this.csOutlineObj.geometry.dispose()
      this.csOutlineObj = null
    }

    super.updateSceneObject()
    
    if(!this.sceneObject)
      return

    // Update display for countersunk circle
    if(this.isCountersunk && this.countersunkRadius > 0) {
      var radius = Math.max(this.radius, 1)
      this.updateDatumLinkWorldPoint()

      var centerX = this.worldPoint.x - this.anchorPoint.x + radius
      var centerY = this.worldPoint.y - this.anchorPoint.y + radius

      var outputShape = new THREE.Shape()
      outputShape.absellipse(centerX, centerY, this.countersunkRadius, this.countersunkRadius, 0, Math.PI*2, false, 0)

      var outlineGeometry = new THREE.BufferGeometry();
      var vertices = []
      var shapePoints = outputShape.getPoints(shapeDivisions)
      for(var i = 0; i < shapePoints.length - 1; i++)
        vertices.push(shapePoints[i].x , shapePoints[i].y, 0, shapePoints[(i + 1) % shapePoints.length].x , shapePoints[(i + 1) % shapePoints.length].y, 0)

      var outlineMaterialInfo = {
        color: this.editor.getTheme().counterSunkColour,
        transparent: true,
        opacity: this.editor.getTheme().counterSunkAlpha,
        dashSize: 6,
        gapSize: 3,
        scale: 1
      }

      outlineGeometry.setAttribute('position', new THREE.Float32BufferAttribute(vertices, 3))
      this.outlineMaterial = new THREE.LineDashedMaterial(outlineMaterialInfo)

      this.outlineObj = new THREE.LineSegments(outlineGeometry, this.outlineMaterial)
      this.outlineObj.computeLineDistances()
      this.sceneObject.add(this.outlineObj)
    }

  }


  /**
   * Checks if a 2D circle intersects with the rectangle hole
   * Returns true if an intersection occurred & sets the closest point on the hole the point is to
   */
  check2DCircleIntersection(point, radius, outIntPoint) {
    let bounds = this.calculateSegmentBounds()
    let center = bounds.getCenter(new THREE.Vector2())

    let distance = center.distanceTo(point)

    // Inside the hole?
    if(distance <= this.radius) {
      outIntPoint.set(point.x, point.y)
      return true
    }

    // Within the intersecting circle radius
    if(distance <= this.radius + radius) {
      var dir = point.clone().sub(center)
      dir.normalize()

      outIntPoint.copy(center.clone().addScaledVector(dir, this.radius))
      return true
    }

    return false
  }

  /**
   * Clone the circle hole
   */
  clone() {    
    var newHole = new CircleHole(this.editor)
    newHole.copy(this)

    return newHole
  }


  /**
   * Copy details from a circle hole to this circle hole
   */
  copy(otherHole) {
    super.copy(otherHole)
    
    this.defaultRadius = otherHole.defaultRadius
    this.radius = otherHole.radius

    this.rebuildHoleShape()
  }


  // Serialize/Deserialize to/from a JSON object
  serialize() {
    var objData = super.serialize()

    objData.defaultRadius = this.defaultRadius
    objData.radius = this.radius

    return objData
  }
  deserialize(object) {
    super.deserialize(object)

    this.defaultRadius = parseFloat(object.defaultRadius || 20)
    this.radius = parseFloat(object.radius || 0)

    this.holeSizeUpdated()
    this.rebuildHoleShape()
  }
}
